import { Button, Card, Chip, LogoCompleto } from "../components/ui";
import { Cargando, ErrorMsg } from "../components/Estados";
import { useApi } from "../lib/useApi";
import { solesCent } from "../lib/money";
import { guiaNumero } from "../lib/impresora";
import { navegar, rutaHome } from "../lib/ruta";
import type { SesionActiva } from "../lib/tipos";

// La venta detrás del QR del ticket (Kevin, 12-oct-2026). Se llega escaneando el ticket con el
// celular: `#/venta/<client_uuid>`. El server solo la devuelve si es de la botica de quien tiene la
// sesión (el dueño, de su cadena); sin sesión, App muestra el login primero y vuelve acá.
// Responde lo que se pregunta con un ticket en la mano: ¿es nuestro?, ¿qué se vendió?, ¿quién lo
// cobró?, ¿sigue vigente o se anuló?

type Item = { id: string; nombre: string; cantidad_presentacion: number; total_cent: number };
type Respuesta = {
  venta: {
    client_uuid: string;
    estado: "completada" | "anulada";
    fecha_hora: string;
    fecha_hora_cliente: string | null;
    sucursal_nombre: string;
    operador_nombre: string | null;
    metodo_pago: string;
    subtotal_sin_igv_cent: number;
    igv_total_cent: number;
    total_cent: number;
    anulada_motivo: string | null;
  };
  items: Item[];
};

const fecha = (iso: string) =>
  new Intl.DateTimeFormat("es-PE", { timeZone: "America/Lima", dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));

export function VentaTicket({ clientUuid, sesion }: { clientUuid: string; sesion: SesionActiva }) {
  const { data, cargando, error, recargar } = useApi<Respuesta>(`/ventas/ticket/${encodeURIComponent(clientUuid)}`);
  const volver = (
    <Button variant="outline" onClick={() => navegar(rutaHome(sesion.usuario.rol))}>
      Volver al inicio
    </Button>
  );

  return (
    <div className="mx-auto flex w-full max-w-[480px] flex-col gap-4 p-4">
      <div className="flex flex-col items-center gap-1 pt-2 text-center">
        <LogoCompleto alto={72} />
        <h1 className="mt-2 text-[18px] font-bold text-ink">Venta del ticket</h1>
        <p className="text-[13px] text-ink-2">Guía N° {guiaNumero(clientUuid)}</p>
      </div>

      {cargando && <Cargando que="la venta" />}

      {!cargando && error && (
        <Card className="items-center gap-3 text-center">
          {/no encontrada/i.test(error) ? (
            <>
              <p className="text-[14px] font-semibold text-ink">Esta venta no aparece en tu botica</p>
              <p className="text-[13px] text-ink-2">
                Puede ser de otra botica, o se cobró sin internet y ese equipo todavía no la envía. Si es de las
                tuyas, vuelve a escanear en unos minutos.
              </p>
            </>
          ) : (
            <ErrorMsg msg={error} onReintentar={recargar} />
          )}
          {volver}
        </Card>
      )}

      {data && (
        <>
          <Card className="gap-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-[15px] font-bold text-ink">{data.venta.sucursal_nombre}</span>
              {data.venta.estado === "anulada" ? (
                <Chip variant="danger">Anulada</Chip>
              ) : (
                <Chip variant="ok">Vigente</Chip>
              )}
            </div>
            {data.venta.estado === "anulada" && data.venta.anulada_motivo && (
              <p className="text-[13px] text-accent-ink">Motivo de la anulación: {data.venta.anulada_motivo}</p>
            )}
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[13px]">
              <dt className="text-ink-3">Fecha</dt>
              <dd className="text-ink">{fecha(data.venta.fecha_hora_cliente ?? data.venta.fecha_hora)}</dd>
              <dt className="text-ink-3">Atendió</dt>
              <dd className="text-ink">{data.venta.operador_nombre ?? "—"}</dd>
              <dt className="text-ink-3">Pago</dt>
              <dd className="capitalize text-ink">{data.venta.metodo_pago}</dd>
            </dl>
          </Card>

          <Card className="gap-2">
            <ul className="flex flex-col gap-2">
              {data.items.map((it) => (
                <li key={it.id} className="flex items-start justify-between gap-3 text-[13.5px]">
                  <span className="text-ink">
                    <span className="font-semibold">{it.cantidad_presentacion} ×</span> {it.nombre}
                  </span>
                  <span className="whitespace-nowrap text-ink">{solesCent(it.total_cent)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-2 flex flex-col gap-1 border-t border-line pt-2 text-[13px]">
              <div className="flex justify-between text-ink-2">
                <span>Subtotal</span>
                <span>{solesCent(data.venta.subtotal_sin_igv_cent)}</span>
              </div>
              <div className="flex justify-between text-ink-2">
                <span>IGV 18%</span>
                <span>{solesCent(data.venta.igv_total_cent)}</span>
              </div>
              <div className="flex justify-between text-[16px] font-bold text-ink">
                <span>Total</span>
                <span>{solesCent(data.venta.total_cent)}</span>
              </div>
            </div>
          </Card>
          <div className="flex justify-center">{volver}</div>
        </>
      )}
    </div>
  );
}
